import React, { createContext, useContext, useState } from 'react'
import CoachServicesPage from './CoachServicesPage'
import { TranslationProvider } from '~/components/shared/TranslationProvider'
import BenefitsProvider, { useBenefits } from '../BenefitsProvider'
import translations from '../locales.json'
import YnsIcon from '../../icons/YnsIcon'
import { Card } from '~/components/_atoms'
import safeParse from '~/lib/safeParse'
import { useTranslation } from '~/hooks/useTranslation'
import ScheduleInner from './ScheduleInner'
import { Button } from '~/lib/ui/button'
import { SERVICE_REQUEST_ICONS } from '../ServiceRequestIcons'
import { cn } from '~/lib/utils'
import { Toaster } from '~/lib/ui/toast'
import { Provider } from 'react-redux'
import { store } from '~/lib/store'
import CandidateReviewFeedbackForm from '../ResumeReview/CandidateFeedback/CandidateReviewFeedbackForm'
import useFormInfo from './useFormInfo'
import UpdateCandidateProfile from '~/components/UpdateCandidateProfile'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useFormatTime } from '~/hooks/useFormatTime'

const queryClient = new QueryClient()

const ServiceDetailContext = createContext(null)

export const useServiceDetailContext = () => useContext(ServiceDetailContext)

const ScheduledCard = ({ serviceRequest }) => {
  const { coach_started: coachStarted, calendly_event: calendlyEvent } =
    serviceRequest
  const { onCancelClick, current_user } = useBenefits()
  const { t } = useTranslation()
  const formatTime = useFormatTime(current_user.locale)

  const { displaySetting, formResponses, attachments } = useFormInfo({
    serviceRequest,
    t,
  })

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-4">
        <YnsIcon name="calendar" className="mr-2 h-20 w-20" />
        <div className="flex flex-grow flex-col gap-4">
          {coachStarted && (
            <div className="text-md font-semibold">
              {t('scheduled_by_coach')}
            </div>
          )}
          <div>
            <span className="text-md font-semibold">{t('schedule_date')} </span>
            {formatTime(calendlyEvent.start_time, 'date')}
          </div>
          <div>
            <span className="text-md font-semibold">{t('schedule_time')} </span>
            {formatTime(calendlyEvent.start_time, 'time')}
          </div>
          {displaySetting === 1 && (
            <FormInfo info={formResponses} attachments={attachments} />
          )}
        </div>
        <div className="flex flex-col items-center gap-2">
          <a
            href={calendlyEvent.join_url}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full text-link"
          >
            <Button variant="coachcta" className="w-full">
              {t('join_meeting')}
            </Button>
          </a>
          <a
            href={calendlyEvent.reschedule_url}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full text-link"
            onClick={onCancelClick}
          >
            <Button variant="outline" className="w-full">
              {t('reschedule_meeting')}
            </Button>
          </a>
          <a
            href={calendlyEvent.cancel_url}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full text-link"
            onClick={onCancelClick}
          >
            <Button variant="outline" className="w-full">
              {t('cancel_meeting')}
            </Button>
          </a>
        </div>
      </div>
      {displaySetting === 2 && (
        <FormInfo info={formResponses} attachments={attachments} />
      )}
    </div>
  )
}

const FormInfo = ({ info, attachments }) => {
  const { t } = useTranslation()

  if (attachments.length > 0 || info.length > 0) {
    return (
      <div className="flex flex-col gap-4">
        <span className="text-lg font-semibold">{t('information_shared')}</span>
        {attachments.length > 0 && (
          <div>
            <div className="flex items-center font-medium">
              <YnsIcon name="attachment" className="mr-2 h-6 w-6" />
              {t('attachments')}
            </div>
            <div className="flex flex-col gap-2">
              {attachments.map((attachment, idx) => {
                const { url, name } = attachment
                return (
                  <div key={`attachment-${idx}`} className="flex items-center">
                    <a href={`/resume/builder/${url}`} className="text-link">
                      {name}
                    </a>
                  </div>
                )
              })}
            </div>
          </div>
        )}
        {info.map((info, index) => {
          const [title, value] = info
          return (
            <div key={index}>
              <span className="font-medium">
                {title.split('(required')[0]}{' '}
              </span>
              <br />
              {value && <span>{value}</span>}
              {!value && (
                <span className="italic">{t('no_information_required')}</span>
              )}
            </div>
          )
        })}
      </div>
    )
  }
  return null
}

function decodeHtmlEntities(str) {
  const txt = document.createElement('textarea')
  txt.innerHTML = str
  return txt.value
}

const CompletedCard = ({ serviceRequest }) => {
  const { t: tForm } = useTranslation()
  const { translationScope, service_request_type, current_user } =
    useServiceDetailContext()
  const { t } = useTranslation(translationScope)
  const formatTime = useFormatTime(current_user.locale)

  const title =
    serviceRequest?.targeted_research_type === 'company'
      ? t('company_title')
      : serviceRequest?.targeted_research_type === 'industry'
        ? t('industry_title')
        : serviceRequest?.targeted_research_type === 'both'
          ? t('both_title')
          : t('title')

  const { coach_documents: coachDocuments, coach_checklists: coachChecklists } =
    serviceRequest || {}
  const hasCoachDocuments = coachDocuments && coachDocuments.length > 0
  const hasCoachChecklists = coachChecklists && coachChecklists.length > 0

  const { formResponses, attachments } = useFormInfo({
    serviceRequest,
    t: tForm,
  })

  return (
    <div className="mt-4 flex flex-col gap-4 rounded-2xl border bg-white p-6 shadow-md">
      {serviceRequest?.completed_at && (
        <div className="text-lg font-bold">
          {formatTime(serviceRequest.completed_at, 'date')}
        </div>
      )}
      <div className="-ml-2 flex items-center gap-2 ">
        <img
          className="w-16"
          src="https://www-yns-com.s3.us-east-2.amazonaws.com/articles/images/article-complete.gif"
          alt="complete short course"
        />
        <div className="text-lg font-medium">
          {safeParse(
            t('coach_feedback_provided', {
              title,
              coach: serviceRequest?.coach.first_name || '',
            }),
          )}
        </div>
      </div>
      <FormInfo info={formResponses} attachments={attachments} />
      <div className="text-lg font-semibold">{t('feedback')}:</div>
      {serviceRequest?.coach_message?.length && (
        <div>{safeParse(decodeHtmlEntities(serviceRequest.coach_message))}</div>
      )}
      {(hasCoachDocuments || hasCoachChecklists) && (
        <div className="flex gap-4">
          {hasCoachDocuments && (
            <div
              className={cn(
                'flex flex-col gap-2',
                hasCoachDocuments && hasCoachChecklists
                  ? 'border-r border-silver-400 pr-8'
                  : '',
              )}
            >
              <div className="flex items-center font-medium">
                <YnsIcon name="attachment" className="mr-2 h-6 w-6" />
                {t('attachments')}:
              </div>
              <div className="flex flex-col gap-2">
                {coachDocuments.map((doc, index) => (
                  <div key={`doc-${index}`} className="flex items-center">
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-link"
                    >
                      {doc.fileName}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}
          {hasCoachChecklists && (
            <div className="flex flex-col gap-2">
              <div className="flex items-center text-lg font-medium">
                <YnsIcon name="checklist" className="mr-2 h-6 w-6" />
                {t('checklists')}:
              </div>
              <div className="flex flex-col gap-2">
                {coachChecklists.map((checklist, index) => (
                  <div key={`checklist-${index}`} className="flex items-center">
                    <a
                      href={checklist.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-link"
                    >
                      {checklist.fileName}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {!serviceRequest?.feedbacked && (
        <>
          <div className="font-medium">{t('admonition_feedback')}</div>
          <div className="!mt-0">
            <CandidateReviewFeedbackForm
              serviceRequestName={t('title')}
              serviceRequestType={service_request_type}
              serviceRequestId={serviceRequest?.id}
              admonition={t('got_feedback')}
            />
          </div>
        </>
      )}
    </div>
  )
}

const Page = ({ ...props }) => {
  const { current_user, coach, service_request_type, service_request_class } =
    props
  const multipleOptionRequestsList = [
    'linked_in_review',
    'mock_interview',
    'cover_letter_review',
    'negotiation_strategy',
    'resume_tailoring',
    'targeted_research',
  ]
  const isMultipleOptionRequest =
    multipleOptionRequestsList.includes(service_request_type)
  const { serviceRequest, hasViewPreviousParam, targetedResearchType } =
    useBenefits()

  const [view, setView] = useState(
    isMultipleOptionRequest &&
      !serviceRequest?.created_at &&
      !hasViewPreviousParam
      ? 'option'
      : 'detail',
  )

  const serviceRequestTypeMap = {
    orientation_call: 'OrientationCall',
    job_search_strategy: 'JobSearchStrategy',
    linked_in_review: 'LinkedInReview',
    interview_prep: 'InterviewPrep',
    networking_strategy: 'NetworkingStrategy',
    mock_interview: 'MockInterview',
    job_search_burnout: 'JobSearchBurnout',
    career_pivot_planning: 'CareerPivotPlanning',
    negotiation_strategy: 'NegotiationStrategy',
    retirement_support: 'RetirementSupport',
    resume_tailoring: 'ResumeTailoring',
    target_company_research: 'TargetCompanyResearch',
    targeted_research: 'TargetedResearch',
    cover_letter_review: 'CoverLetterReview',
    onboarding_support: 'OnboardingSupport',
    general_check_in: 'GeneralCheckIn',
    salary_research: 'SalaryResearch',
    strengths_assessment: 'StrengthsAssessment',
    business_card_creation: 'BusinessCardCreation',
    executive_bio: 'ExecutiveBio',
    recruiter_guidance_support: 'RecruiterGuidanceSupport',
    board_search: 'BoardSearch',
    resume_writing: 'ResumeWriting',
  }

  const translationScope = serviceRequestTypeMap[service_request_type]

  const contextValue = {
    translationScope,
    service_request_type,
    view,
    setView,
    coach,
    current_user,
  }

  const ynsIcon = SERVICE_REQUEST_ICONS[service_request_class] || 'phone'

  const calendlyEvent = serviceRequest ? serviceRequest?.calendly_event : null

  const { t } = useTranslation(translationScope)

  const serviceRequestCompleted = serviceRequest?.completed || false

  const scheduled = calendlyEvent && !calendlyEvent.service_request_no_show_id

  const cancelled = serviceRequest?.status === 'cancelled'

  const serviceRequestStatus = () => {
    if (serviceRequestCompleted) {
      return t('completed')
    } else if (scheduled) {
      return t('scheduled')
    } else if (serviceRequest?.created_at && !cancelled) {
      return t('in_progress')
    } else if (view === 'option') {
      return t('please_select_option')
    } else {
      return t('get_started')
    }
  }

  const title =
    targetedResearchType === 'company'
      ? t('company_title')
      : targetedResearchType === 'industry'
        ? t('industry_title')
        : targetedResearchType === 'both'
          ? t('both_title')
          : t('title')

  return (
    <ServiceDetailContext.Provider value={contextValue}>
      <div className="flex flex-col gap-6">
        <div className="mt-1 flex items-center font-roboto-slab text-xl text-green-800">
          <YnsIcon name={ynsIcon} className="mr-2 h-6 w-6" />
          {title} - {serviceRequestStatus()}
        </div>
        {view === 'option' && <OptionView />}
        {view === 'detail' && <DetailView />}
        <Toaster />
      </div>
    </ServiceDetailContext.Provider>
  )
}

const OptionView = () => {
  const { translationScope } = useServiceDetailContext()
  const { serviceRequest } = useBenefits()
  const { t } = useTranslation(translationScope)

  const optionsKeys = Object.keys(t('options')).filter(
    (key) => !isNaN(Number(key)),
  )
  const infoCards = optionsKeys.map((key) => ({
    icon: t(`options.${key}.icon`),
    title: t(`options.${key}.title`),
    content:
      safeParse(t(`options.${key}.content`)) ||
      'This is a placeholder for the card content.',
    cta: t(`options.${key}.cta`),
    type: t(`options.${key}.type`),
    url: t(`options.${key}.url`),
  }))

  return (
    <div>
      <div className="grid gap-6 md:grid-cols-2">
        {infoCards.map((card, index) => (
          <OptionCard card={card} key={index} />
        ))}
      </div>
      {serviceRequest?.completed && (
        <CompletedCard serviceRequest={serviceRequest} />
      )}
      <CompletedRequests />
    </div>
  )
}

const OptionCard = ({ card }) => {
  const { title, content, type, url, cta, icon } = card
  const { setView } = useServiceDetailContext()
  const { setMeetingViaCalendly, setTargetedResearchType } = useBenefits()
  const setNoMeeting = () => {
    setMeetingViaCalendly(false)
    setView('detail')
  }
  const setTargetedResearch = (type) => {
    setMeetingViaCalendly(false)
    setTargetedResearchType(type)
    setView('detail')
  }

  const handleCardClick = () => {
    type == 'review'
      ? setNoMeeting()
      : type == 'schedule'
        ? setView('detail')
        : type == 'company_research'
          ? setTargetedResearch('company')
          : type == 'industry_research'
            ? setTargetedResearch('industry')
            : type == 'industry_and_companies_research'
              ? setTargetedResearch('both')
              : null
  }
  return (
    <Card color="secondary" className="p-4">
      <YnsIcon name={icon} className="mb-2 !h-16 !w-16" />
      <h3 className="mb-2 flex items-center gap-2 !font-roboto text-lg font-semibold">
        {title}
      </h3>
      <div className="text-md leading-relaxed text-gray-700">{content}</div>
      <Button
        variant="coachcta"
        className={cn('mt-4', type === 'chat' ? 'open-chat-modal' : '')}
        data-toggle={type === 'chat' ? 'modal' : undefined}
        data-target={type === 'chat' ? '#coach_chat_list' : undefined}
        onClick={handleCardClick}
      >
        {type === 'link' ? (
          <a
            href={url}
            className="!text-white text-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            {cta}
          </a>
        ) : (
          cta
        )}
      </Button>
    </Card>
  )
}

const DetailView = () => {
  const { translationScope, coach, current_user, service_request_type } =
    useServiceDetailContext()
  const {
    serviceRequest,
    meetingViaCalendly,
    setMeetingViaCalendly,
    hasViewPreviousParam,
    targetedResearchType,
    select,
  } = useBenefits()
  const { t } = useTranslation()
  const additionalCardScope =
    targetedResearchType === 'company'
      ? 'company.'
      : targetedResearchType === 'industry'
        ? 'industry.'
        : targetedResearchType === 'both'
          ? 'both.'
          : ''

  React.useEffect(() => {
    if (
      service_request_type === 'targeted_research' ||
      service_request_type === 'target_company_research' ||
      service_request_type === 'salary_research' ||
      service_request_type === 'business_card_creation'
    ) {
      setMeetingViaCalendly(false)
    }
  }, [service_request_type, setMeetingViaCalendly])

  const isLiaison = coach?.isLiaison || false
  const serviceRequestCompleted = serviceRequest?.completed || false
  const calendlyEvent = serviceRequest ? serviceRequest?.calendly_event : null
  const noShow = calendlyEvent?.service_request_no_show_id
  const introCallCompleted = current_user.orientationCallCompleted
  const useProgramLevelTranslationKeys =
    service_request_type == 'target_company_research'
  const cardTranslationScope = useProgramLevelTranslationKeys
    ? translationScope + '.' + current_user.candidate_program_level
    : translationScope
  const infoCards = [
    {
      title: meetingViaCalendly
        ? t(`${cardTranslationScope}.cards.1.title`)
        : t(
            `${cardTranslationScope}.cards.${additionalCardScope}1.title_no_meeting`,
          ),
      content:
        safeParse(
          meetingViaCalendly
            ? t(`${cardTranslationScope}.cards.1.content`)
            : t(
                `${cardTranslationScope}.cards.${additionalCardScope}1.content_no_meeting`,
              ),
        ) ||
        'This is a placeholder for the first card content. Please replace it with actual content.',
    },
    {
      title: meetingViaCalendly
        ? t(`${cardTranslationScope}.cards.2.title`)
        : t(
            `${cardTranslationScope}.cards.${additionalCardScope}2.title_no_meeting`,
          ),
      content:
        safeParse(
          meetingViaCalendly
            ? t(`${cardTranslationScope}.cards.2.content`)
            : t(
                `${cardTranslationScope}.cards.${additionalCardScope}2.content_no_meeting`,
              ),
        ) ||
        'This is a placeholder for the second card content. Please replace it with actual content.',
    },
    {
      title: meetingViaCalendly
        ? t(`${cardTranslationScope}.cards.3.title`)
        : t(
            `${cardTranslationScope}.cards.${additionalCardScope}3.title_no_meeting`,
          ),
      content:
        safeParse(
          meetingViaCalendly
            ? t(`${cardTranslationScope}.cards.3.content`)
            : t(
                `${cardTranslationScope}.cards.${additionalCardScope}3.content_no_meeting`,
              ),
        ) ||
        'This is a placeholder for the third card content. Please replace it with actual content.',
    },
    {
      title: meetingViaCalendly
        ? t(`${cardTranslationScope}.cards.4.title`)
        : t(
            `${cardTranslationScope}.cards.${additionalCardScope}4.title_no_meeting`,
          ),
      content:
        safeParse(
          meetingViaCalendly
            ? t(`${cardTranslationScope}.cards.4.content`)
            : t(
                `${cardTranslationScope}.cards.${additionalCardScope}4.content_no_meeting`,
              ),
        ) ||
        'This is a placeholder for the fourth card content. Please replace it with actual content.',
    },
  ]
  const scheduled =
    !serviceRequestCompleted &&
    calendlyEvent &&
    !calendlyEvent.cancelled_at &&
    !noShow

  const needsToBeScheduled =
    noShow ||
    !serviceRequest?.created_at ||
    (!calendlyEvent && serviceRequest.meeting_via_calendly)

  const [showUpdateProfileDialog, setShowUpdateProfileDialog] = useState(false)

  const toggleShowUpdateProfileDialog = () => {
    setShowUpdateProfileDialog(!showUpdateProfileDialog)
  }
  return (
    <div>
      {!serviceRequest?.completed && !hasViewPreviousParam && (
        <div className="space-y-4 rounded-2xl border bg-white p-6 shadow-md">
          {scheduled && <ScheduledCard serviceRequest={serviceRequest} />}
          {needsToBeScheduled && (
            <div>
              {!isLiaison &&
                introCallCompleted &&
                service_request_type !== 'onboarding_support' &&
                service_request_type !== 'strengths_assessment' && (
                  <div className="mb-4 text-lg font-medium">
                    {t('get_started_here')}
                  </div>
                )}
              <ScheduleInner
                current_user={current_user}
                coach={coach}
                toggleShowUpdateProfileDialog={toggleShowUpdateProfileDialog}
              />
              <UpdateCandidateProfile
                selectProgram={select}
                showUpdateProfileDialog={showUpdateProfileDialog}
                toggleShowUpdateProfileDialog={toggleShowUpdateProfileDialog}
              />
            </div>
          )}
          {serviceRequest?.created_at &&
            !serviceRequestCompleted &&
            !serviceRequest?.meeting_via_calendly && (
              <div className="flex flex-col gap-6">
                {service_request_type === 'target_company_research' ||
                service_request_type === 'targeted_research' ||
                service_request_type === 'salary_research' ||
                service_request_type === 'business_card_creation' ? (
                  <p>
                    {t(`${translationScope}.research_team_reviewing_request`)}
                  </p>
                ) : (
                  <p>
                    {t(`${translationScope}.coach_reviewing_request`, {
                      coach: coach.first_name,
                    })}
                  </p>
                )}
              </div>
            )}
        </div>
      )}
      {serviceRequest?.completed && (
        <CompletedCard serviceRequest={serviceRequest} />
      )}
      <CompletedRequests />
      {/* Info Sections */}
      <div className="mt-6 grid gap-6 md:grid-cols-2">
        {infoCards.map((card, index) => (
          <Card key={index} color="secondary" className="p-4">
            <YnsIcon name="tips" className="mb-2 h-6 w-6" />
            <h3 className="mb-2 flex items-center gap-2 !font-roboto text-lg font-semibold">
              {card.title}
            </h3>
            <div className="text-md leading-relaxed text-gray-700">
              {card.content}
            </div>
          </Card>
        ))}
      </div>
    </div>
  )
}

const CompletedRequests = () => {
  const { serviceRequest } = useBenefits()
  const completedReoccurringRequests =
    (serviceRequest && serviceRequest?.completed_reoccurring_requests) || []

  // Filter out the current serviceRequest to avoid duplicate CompletedCard
  const filteredRequests = completedReoccurringRequests.filter(
    (request) => request.id !== serviceRequest?.id,
  )

  return (
    <>
      {filteredRequests.map((request, index) => (
        <div key={`completed-request-${index}`}>
          <CompletedCard serviceRequest={request} />
        </div>
      ))}
    </>
  )
}

export const BackToServices = () => {
  const { t } = useTranslation()

  return (
    <a
      href="/coach_services/professional_services"
      className="flex items-center gap-2 text-link"
    >
      <YnsIcon name="return-left" className="h-6 w-6" />
      {t('back_link')}
    </a>
  )
}

const ServiceDetail = ({ ...props }) => {
  const { current_user } = props
  return (
    <QueryClientProvider client={queryClient}>
      <TranslationProvider
        locale={current_user.locale}
        translations={translations}
        scope="javascript.components.coach_services.ProfessionalServices"
      >
        <Provider store={store}>
          <BenefitsProvider {...props}>
            <CoachServicesPage description={<BackToServices />}>
              <Page {...props} />
            </CoachServicesPage>
          </BenefitsProvider>
        </Provider>
      </TranslationProvider>
    </QueryClientProvider>
  )
}

export default ServiceDetail
